import Big from 'big.js';

import { getBarcodeError, getCAPError, getCPIError, getGTINError, getPriceError, getQuantityError, getRetailPriceError, getSKUError, getWholesalePriceError } from '../validations/inventory';
import { getHeightError, getLengthError, getProcessingProfileError, getWeightError, getWidthError } from '../validations/shipping';
import { getCharactersCountError, getInstructionsError } from '../validations/personalization';
import { getDescriptionError } from '../validations/description';
import { getTitleError } from '../validations/title';
import { getSize } from '../iterable/getSize';
import { toJS } from '../iterable/toJS';
import { get } from '../iterable/get';

import { FILTER, MENU_ITEM, OPERATIONS } from '../../constants/bulkEdit';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { SELECTION } from '../../constants/listings';
import { EMPTY } from '../../constants';

export function getMenuItemByAttribute({ attribute, channel }) {
  switch (attribute) {
    case ATTRIBUTE.BARCODE: {
      return MENU_ITEM.BARCODE;
    }

    case ATTRIBUTE.CAP: {
      return MENU_ITEM.CAP;
    }

    case ATTRIBUTE.COUNTRY:
    case ATTRIBUTE.LOCATION:
    case ATTRIBUTE.POSTAL_CODE: {
      return MENU_ITEM.ITEM_LOCATION;
    }

    case ATTRIBUTE.COUNTRY_CODE: {
      return MENU_ITEM.COUNTRY_CODE;
    }

    case ATTRIBUTE.CPI: {
      return MENU_ITEM.CPI;
    }

    case ATTRIBUTE.DEPTH:
    case ATTRIBUTE.HEIGHT:
    case ATTRIBUTE.LENGTH:
    case ATTRIBUTE.WIDTH: {
      return MENU_ITEM.ITEM_SIZE;
    }

    case ATTRIBUTE.HS_CODE: {
      return MENU_ITEM.HS_CODE;
    }

    case ATTRIBUTE.PAYMENT_POLICY: {
      return MENU_ITEM.PAYMENT_POLICY;
    }

    case ATTRIBUTE.PRICE: {
      return MENU_ITEM.PRICE;
    }

    case ATTRIBUTE.PROFILE_ID: {
      return MENU_ITEM.SHIPPING_PROFILE;
    }

    case ATTRIBUTE.QUANTITY:
    case ATTRIBUTE.TRACK_QUANTITY: {
      return MENU_ITEM.QUANTITY;
    }

    case ATTRIBUTE.PROCESSING_PROFILE: {
      return MENU_ITEM.PROCESSING_PROFILE;
    }

    case ATTRIBUTE.RETURN_POLICY: {
      return MENU_ITEM.RETURN_POLICY;
    }

    case ATTRIBUTE.SHIPPING_POLICY: {
      return MENU_ITEM.SHIPPING_POLICY;
    }

    case ATTRIBUTE.SKU: {
      return MENU_ITEM.SKU;
    }

    case ATTRIBUTE.UNIT: {
      return MENU_ITEM.WEIGHT;
    }

    case ATTRIBUTE.WEIGHT: {
      switch (channel) {
        case EBAY:
        case ETSY: {
          return MENU_ITEM.ITEM_WEIGHT;
        }

        case SHOPIFY: {
          return MENU_ITEM.WEIGHT;
        }

        default: {
          return undefined;
        }
      }
    }

    case ATTRIBUTE.WEIGHT_MAJOR:
    case ATTRIBUTE.WEIGHT_MINOR: {
      return MENU_ITEM.ITEM_WEIGHT;
    }

    default: {
      return undefined;
    }
  }
}

export function getError({ type, ...rest }) {
  switch (type) {
    case ATTRIBUTE.BARCODE: {
      return getBarcodeError({ ...rest });
    }

    case ATTRIBUTE.CAP: {
      return getCAPError({ ...rest });
    }

    case ATTRIBUTE.CHAR_COUNT_MAX: {
      return getCharactersCountError({ ...rest });
    }

    case ATTRIBUTE.CPI: {
      return getCPIError({ ...rest });
    }

    case ATTRIBUTE.DESCRIPTION: {
      return getDescriptionError({ ...rest });
    }

    case ATTRIBUTE.GTIN: {
      return getGTINError({ ...rest });
    }

    case ATTRIBUTE.HEIGHT: {
      return getHeightError({ ...rest });
    }

    case ATTRIBUTE.INSTRUCTIONS: {
      return getInstructionsError({ ...rest });
    }

    case ATTRIBUTE.LENGTH: {
      return getLengthError({ ...rest });
    }

    case ATTRIBUTE.PRICE: {
      return getPriceError({ ...rest });
    }

    case ATTRIBUTE.PROCESSING_PROFILE: {
      return getProcessingProfileError({ ...rest });
    }

    case ATTRIBUTE.RETAIL_PRICE: {
      return getRetailPriceError({ ...rest });
    }

    case ATTRIBUTE.QUANTITY: {
      return getQuantityError({ ...rest });
    }

    case ATTRIBUTE.SKU: {
      return getSKUError({ ...rest });
    }

    case ATTRIBUTE.TITLE: {
      return getTitleError({ ...rest });
    }

    case ATTRIBUTE.WEIGHT: {
      return getWeightError({ ...rest });
    }

    case ATTRIBUTE.WHOLESALE_PRICE: {
      return getWholesalePriceError({ ...rest });
    }

    case ATTRIBUTE.WIDTH: {
      return getWidthError({ ...rest });
    }

    default: {
      return false;
    }
  }
}

export function getNumberValue({ decimals, operationType, operationValue, productValue }) {
  const type = (/[^\.]+$/.exec(operationType) || [])[0];
  const number = new Big(operationValue || 0);
  let result = new Big(productValue || 0);

  switch (type) {
    case 'set':
    case 'changeTo': {
      if (number) {
        result = number;
      }

      break;
    }

    case 'increaseBy': {
      result = result.plus(number);
      break;
    }

    case 'decreaseBy': {
      result = result.minus(number);
      break;
    }

    default:
      break;
  }

  return Number.isFinite(decimals)
    ? result.toFixed(decimals)
    : result.toString();
}

export function getPriceValue({ absolute, cents, operationType, operationValue, productValue }) {
  const type = (/[^\.]+$/.exec(operationType) || [])[0];
  const number = new Big(operationValue || 0);
  const hundred = Big(100);
  let result = new Big(productValue || 0);

  switch (type) {
    case 'set':
    case 'changeTo': {
      if (number) {
        result = number;
      }

      break;
    }

    case 'increaseBy': {
      if (number.gt(0)) {
        if (absolute) {
          result = result.plus(number);
        } else {
          result = result.plus(result.times(number.div(hundred)));
        }
      }

      break;
    }

    case 'decreaseBy': {
      if (number.gt(0)) {
        if (absolute) {
          result = result.minus(number);
        } else {
          result = result.minus(result.times(number.div(hundred)));
        }
      }

      break;
    }

    default:
      break;
  }

  if (cents) {
    result = result.round(0, 0)[result.s > 0 ? 'plus' : 'minus'](new Big(cents).div(hundred));
  }

  return result.toFixed(2);
}

function shouldAddMenuItemFilters({ item, menu }) {
  switch (item) {
    case MENU_ITEM.FILES: {
      return menu.get('type') !== ATTRIBUTE.DIGITAL;
    }

    case MENU_ITEM.ITEM_SIZE:
    case MENU_ITEM.ITEM_WEIGHT:
    case MENU_ITEM.PROCESSING_PROFILE:
    case MENU_ITEM.RETURN_POLICY:
    case MENU_ITEM.SHIPPING_PROFILE:
    case MENU_ITEM.VARIATIONS: {
      return menu.get('type') !== ATTRIBUTE.PHYSICAL;
    }

    default: {
      if (menu.getIn(['items', item, 'isTaxonomy'])) {
        return getSize(menu.getIn(['items', item, 'filters'])) > 0;
      }

      return false;
    }
  }
}

export function updateFiltersForMenuItem({ channel, filters, item: current, menu, products }) {
  const previous = menu.getIn(['selected', 'item']);

  if (!shouldAddMenuItemFilters({ item: current, menu }) && shouldAddMenuItemFilters({ item: previous, menu })) {
    if (!filters.get(FILTER[channel].TITLE)) {
      return filters.set('offset', 0).set('ids', products.get('all'));
    }

    return filters.set('offset', 0).delete('ids');
  }

  switch (current) {
    case MENU_ITEM.FILES: {
      return (
        shouldAddMenuItemFilters({ item: current, menu }) &&
        menu.getIn(['items', previous, 'filters', FILTER[channel].IS_DIGITAL]) !== true
      )
        ? filters.set('offset', 0).set('ids', products.get(ATTRIBUTE.DIGITAL))
        : undefined;
    }

    case MENU_ITEM.ITEM_SIZE:
    case MENU_ITEM.ITEM_WEIGHT:
    case MENU_ITEM.PROCESSING_PROFILE:
    case MENU_ITEM.RETURN_POLICY:
    case MENU_ITEM.SHIPPING_PROFILE:
    case MENU_ITEM.VARIATIONS: {
      return (
        shouldAddMenuItemFilters({ item: current, menu }) &&
        menu.getIn(['items', previous, 'filters', FILTER[channel].IS_DIGITAL]) !== false
      )
        ? filters.set('offset', 0).set('ids', products.get(ATTRIBUTE.PHYSICAL))
        : undefined;
    }

    default: {
      if (menu.getIn(['items', current, 'isTaxonomy'])) {
        if (!shouldAddMenuItemFilters({ item: current, menu })) {
          if (shouldAddMenuItemFilters({ item: previous, menu })) {
            if (!filters.get(FILTER[channel].TITLE)) {
              return filters.set('offset', 0).set('ids', products.get('all'));
            } else {
              return filters.set('offset', 0).delete('ids');
            }
          }
        } else if (
          !menu.getIn(['items', previous, 'isTaxonomy']) ||
          !shouldAddMenuItemFilters({ item: previous, menu }) ||
          !menu.getIn(['items', current, 'filters', FILTER[channel].TAXONOMY_IDS]).equals(
            menu.getIn(['items', previous, 'filters', FILTER[channel].TAXONOMY_IDS])
          )
        ) {
          return filters.set('offset', 0).delete('ids');
        }
      }

      return undefined;
    }
  }
}

export function getFilterIdsForMenuItem({ menu, products }) {
  const selectedMenuItem = menu.getIn(['selected', 'item']);

  if (shouldAddMenuItemFilters({ item: selectedMenuItem, menu })) {
    switch (selectedMenuItem) {
      case MENU_ITEM.FILES: {
        return products.get(ATTRIBUTE.DIGITAL);
      }

      case MENU_ITEM.ITEM_SIZE:
      case MENU_ITEM.ITEM_WEIGHT:
      case MENU_ITEM.PROCESSING_PROFILE:
      case MENU_ITEM.RETURN_POLICY:
      case MENU_ITEM.SHIPPING_PROFILE:
      case MENU_ITEM.VARIATIONS: {
        return products.get(ATTRIBUTE.PHYSICAL);
      }

      default: {
        if (
          menu.getIn(['items', selectedMenuItem, 'isTaxonomy']) &&
          shouldAddMenuItemFilters({ item: selectedMenuItem, menu })
        ) {
          return undefined;
        }

        return products.get('all');
      }
    }
  }

  return products.get('all');
}

export function shapeFiltersForAPI({ channel, errors, filters: source, menu, products, selection }) {
  const selectedMenuItem = menu.getIn(['selected', 'item']);
  const menuFilters = shouldAddMenuItemFilters({ item: selectedMenuItem, menu })
    ? menu.getIn(['items', selectedMenuItem, 'filters'])
    : EMPTY.MAP;

  const filters = {};

  if (selection === SELECTION.INCOMPLETE) {
    filters.ids = errors.get('products').toArray();
  } else if (menuFilters.has(FILTER[channel].IS_DIGITAL)) {
    if (menuFilters.get(FILTER[channel].IS_DIGITAL)) {
      filters.ids = products.get(ATTRIBUTE.DIGITAL).toArray();
    } else {
      filters.ids = products.get(ATTRIBUTE.PHYSICAL).toArray();
    }
  } else {
    filters.ids = products.get('all').toArray();
  }

  if (getSize(menuFilters)) {
    for (const [key, value] of menuFilters.entries()) {
      if (key !== FILTER[channel].IS_DIGITAL) {
        filters[key] = toJS(value);
      }
    }
  }

  if (source.get(FILTER[channel].TITLE)) {
    filters[FILTER[channel].TITLE] = source.get(FILTER[channel].TITLE);
  }

  return filters;
}

export function getUpdatedMenuItemsFromOperations({ channel, operations }) {
  function getAiUpdatedMenuItem(opetation) {
    switch (opetation.getIn(['value', 'destination_field'])) {
      case ATTRIBUTE.DESCRIPTION: {
        return MENU_ITEM.DESCRIPTION;
      }

      case ATTRIBUTE.TAGS: {
        return MENU_ITEM.TAGS;
      }

      case ATTRIBUTE.TITLE: {
        return MENU_ITEM.TITLE;
      }

      default: {
        return undefined;
      }
    }
  }

  function reduceEbayOperations(updates, operation) {
    switch (operation.get('type')) {
      case OPERATIONS[EBAY].AI: {
        const menuItem = getAiUpdatedMenuItem(operation);
        return menuItem
          ? updates.add(menuItem)
          : updates;
      }

      case OPERATIONS[EBAY].ITEM_CATEGORY: {
        return updates.add(MENU_ITEM.ITEM_CATEGORY);
      }

      case OPERATIONS[EBAY].COUNTRY:
      case OPERATIONS[EBAY].LOCATION:
      case OPERATIONS[EBAY].POSTAL_CODE: {
        return updates.add(MENU_ITEM.ITEM_LOCATION);
      }

      case OPERATIONS[EBAY].DEPTH.SET:
      case OPERATIONS[EBAY].LENGTH.SET:
      case OPERATIONS[EBAY].WIDTH.SET: {
        return updates.add(MENU_ITEM.ITEM_SIZE);
      }

      case OPERATIONS[EBAY].DESCRIPTION.SET: {
        return updates.add(MENU_ITEM.DESCRIPTION);
      }

      case OPERATIONS[EBAY].ITEM_SPECIFICS.SET: {
        return updates.union(operation.get('value').map(get('name')).toSet());
      }

      case OPERATIONS[EBAY].ITEM_SPECIFICS.UPDATE: {
        return updates.union(operation.get('value').keySeq().toSet());
      }

      case OPERATIONS[EBAY].PAYMENT_POLICY: {
        return updates.add(MENU_ITEM.PAYMENT_POLICY);
      }

      case OPERATIONS[EBAY].PHOTOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.PHOTOS);
      }

      case OPERATIONS[EBAY].PRICE.CHANGE_TO: {
        return updates.add(MENU_ITEM.PRICE);
      }

      case OPERATIONS[EBAY].QUANTITY.CHANGE_TO: {
        return updates.add(MENU_ITEM.QUANTITY);
      }

      case OPERATIONS[EBAY].RETURN_POLICY: {
        return updates.add(MENU_ITEM.RETURN_POLICY);
      }

      case OPERATIONS[EBAY].SHIPPING_POLICY: {
        return updates.add(MENU_ITEM.SHIPPING_POLICY);
      }

      case OPERATIONS[EBAY].SKU.CHANGE_TO: {
        return updates.add(MENU_ITEM.SKU);
      }

      case OPERATIONS[EBAY].STORE_CATEGORY: {
        return updates.add(MENU_ITEM.STORE_CATEGORY);
      }

      case OPERATIONS[EBAY].TITLE.SET: {
        return updates.add(MENU_ITEM.TITLE);
      }

      case OPERATIONS[EBAY].VARIATIONS: {
        return updates.add(MENU_ITEM.VARIATIONS);
      }

      case OPERATIONS[EBAY].VIDEOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.VIDEOS);
      }

      case OPERATIONS[EBAY].WEIGHT_MAJOR.SET:
      case OPERATIONS[EBAY].WEIGHT_MINOR.SET: {
        return updates.add(MENU_ITEM.ITEM_WEIGHT);
      }

      default: {
        return updates;
      }
    }
  }

  function reduceEtsyOperations(updates, operation) {
    switch (operation.get('type')) {
      case OPERATIONS[ETSY].AI: {
        const menuItem = getAiUpdatedMenuItem(operation);
        return menuItem
          ? updates.add(menuItem)
          : updates;
      }

      case OPERATIONS[ETSY].CATEGORY: {
        return updates.add(MENU_ITEM.CATEGORY);
      }

      case OPERATIONS[ETSY].DESCRIPTION.SET: {
        return updates.add(MENU_ITEM.DESCRIPTION);
      }

      case OPERATIONS[ETSY].FILES.CHANGE_TO: {
        return updates.add(MENU_ITEM.FILES);
      }

      case OPERATIONS[ETSY].HEIGHT.SET:
      case OPERATIONS[ETSY].LENGTH.SET:
      case OPERATIONS[ETSY].WIDTH.SET: {
        return updates.add(MENU_ITEM.ITEM_SIZE);
      }

      case OPERATIONS[ETSY].IS_SUPPLY:
      case OPERATIONS[ETSY].WHEN_MADE:
      case OPERATIONS[ETSY].WHO_MADE: {
        return updates.add(MENU_ITEM.ABOUT);
      }

      case OPERATIONS[ETSY].MATERIALS.CHANGE_TO: {
        return updates.add(MENU_ITEM.MATERIALS);
      }

      case OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET:
      case OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET:
      case OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET:
      case OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET:
      case OPERATIONS[ETSY].PERSONALIZATION_QUESTIONS: {
        return updates.add(MENU_ITEM.PERSONALIZATION);
      }

      case OPERATIONS[ETSY].PHOTOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.PHOTOS);
      }

      case OPERATIONS[ETSY].PRICE.CHANGE_TO: {
        return updates.add(MENU_ITEM.PRICE);
      }

      case OPERATIONS[ETSY].PROCESSING_PROFILE: {
        return updates.add(MENU_ITEM.PROCESSING_PROFILE);
      }

      case OPERATIONS[ETSY].PRODUCTION_PARTNERS: {
        return updates.add(MENU_ITEM.PRODUCTION_PARTNER);
      }

      case OPERATIONS[ETSY].QUANTITY.CHANGE_TO: {
        return updates.add(MENU_ITEM.QUANTITY);
      }

      case OPERATIONS[ETSY].RETURN_POLICY: {
        return updates.add(MENU_ITEM.RETURN_POLICY);
      }

      case OPERATIONS[ETSY].SHIPPING_PROFILE: {
        return updates.add(MENU_ITEM.SHIPPING_PROFILE);
      }

      case OPERATIONS[ETSY].SKU.CHANGE_TO: {
        return updates.add(MENU_ITEM.SKU);
      }

      case OPERATIONS[ETSY].TAGS.CHANGE_TO: {
        return updates.add(MENU_ITEM.TAGS);
      }

      case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES: {
        return updates.union(operation.get('value').keySeq().map(String).toSet());
      }

      case OPERATIONS[ETSY].TITLE.SET: {
        return updates.add(MENU_ITEM.TITLE);
      }

      case OPERATIONS[ETSY].VARIATIONS: {
        return updates.add(MENU_ITEM.VARIATIONS);
      }

      case OPERATIONS[ETSY].VIDEOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.VIDEOS);
      }

      case OPERATIONS[ETSY].WEIGHT.SET: {
        return updates.add(MENU_ITEM.ITEM_WEIGHT);
      }

      default: {
        return updates;
      }
    }
  }

  function reduceFaireOperations(updates, operation) {
    switch (operation.get('type')) {
      case OPERATIONS[FAIRE].AI: {
        const menuItem = getAiUpdatedMenuItem(operation);
        return menuItem
          ? updates.add(menuItem)
          : updates;
      }

      case OPERATIONS[FAIRE].PHOTOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.PHOTOS);
      }

      case OPERATIONS[FAIRE].TITLE.SET: {
        return updates.add(MENU_ITEM.TITLE);
      }

      case OPERATIONS[FAIRE].DESCRIPTION.SET: {
        return updates.add(MENU_ITEM.DESCRIPTION);
      }

      case OPERATIONS[FAIRE].CATEGORY: {
        return updates.add(MENU_ITEM.CATEGORY);
      }

      case OPERATIONS[FAIRE].PRICES.SET: {
        return updates.add(MENU_ITEM.PRICE);
      }

      case OPERATIONS[FAIRE].QUANTITY.CHANGE_TO: {
        return updates.add(MENU_ITEM.QUANTITY);
      }

      case OPERATIONS[FAIRE].RESTOCK_DATE.SET: {
        return updates.add(MENU_ITEM.QUANTITY);
      }

      case OPERATIONS[FAIRE].SKU.CHANGE_TO: {
        return updates.add(MENU_ITEM.SKU);
      }

      case OPERATIONS[FAIRE].GTIN.CHANGE_TO: {
        return updates.add(MENU_ITEM.GTIN);
      }

      case OPERATIONS[FAIRE].CONTINUE_SELLING.SET: {
        return updates.add(MENU_ITEM.QUANTITY);
      }

      case OPERATIONS[FAIRE].PREORDER.SET: {
        return updates.add(MENU_ITEM.AVAILABILITY);
      }

      case OPERATIONS[FAIRE].START_DATE.SET: {
        return updates.add(MENU_ITEM.AVAILABILITY);
      }

      case OPERATIONS[FAIRE].END_DATE.SET: {
        return updates.add(MENU_ITEM.AVAILABILITY);
      }

      case OPERATIONS[FAIRE].DEADLINE_DATE.SET: {
        return updates.add(MENU_ITEM.AVAILABILITY);
      }

      case OPERATIONS[FAIRE].KEEP_ACTIVE.SET: {
        return updates.add(MENU_ITEM.AVAILABILITY);
      }

      case OPERATIONS[FAIRE].CASE_SIZE.SET: {
        return updates.add(MENU_ITEM.SELLING_METHOD);
      }

      case OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY.SET: {
        return updates.add(MENU_ITEM.SELLING_METHOD);
      }

      case OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY.SET: {
        return updates.add(MENU_ITEM.SELLING_METHOD);
      }

      case OPERATIONS[FAIRE].TARIFF_CODE.SET: {
        return updates.add(MENU_ITEM.TARIFF_CODE);
      }

      case OPERATIONS[FAIRE].DISTANCE_UNIT: {
        return updates.add(MENU_ITEM.ITEM_SIZE);
      }

      case OPERATIONS[FAIRE].HEIGHT.SET: {
        return updates.add(MENU_ITEM.ITEM_SIZE);
      }

      case OPERATIONS[FAIRE].LENGTH.SET: {
        return updates.add(MENU_ITEM.ITEM_SIZE);
      }

      case OPERATIONS[FAIRE].VARIATIONS: {
        return updates.add(MENU_ITEM.VARIATIONS);
      }

      case OPERATIONS[FAIRE].WIDTH.SET: {
        return updates.add(MENU_ITEM.ITEM_SIZE);
      }

      case OPERATIONS[FAIRE].MASS_UNIT: {
        return updates.add(MENU_ITEM.ITEM_WEIGHT);
      }

      case OPERATIONS[FAIRE].WEIGHT.SET: {
        return updates.add(MENU_ITEM.ITEM_WEIGHT);
      }

      default: {
        return updates;
      }
    }
  }

  function reduceShopifyOperations(updates, operation) {
    switch (operation.get('type')) {
      case OPERATIONS[SHOPIFY].AI: {
        const menuItem = getAiUpdatedMenuItem(operation);
        return menuItem
          ? updates.add(menuItem)
          : updates;
      }

      case OPERATIONS[SHOPIFY].COLLECTIONS: {
        return updates.add(MENU_ITEM.COLLECTIONS);
      }

      case OPERATIONS[SHOPIFY].DESCRIPTION.SET: {
        return updates.add(MENU_ITEM.DESCRIPTION);
      }

      case OPERATIONS[SHOPIFY].META_DESCRIPTION.SET: {
        return updates.add(MENU_ITEM.META_DESCRIPTION);
      }

      case OPERATIONS[SHOPIFY].PAGE_TITLE.SET: {
        return updates.add(MENU_ITEM.PAGE_TITLE);
      }

      case OPERATIONS[SHOPIFY].PHOTOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.PHOTOS);
      }

      case OPERATIONS[SHOPIFY].PRODUCT_TYPE: {
        return updates.add(MENU_ITEM.PRODUCT_TYPE);
      }

      case OPERATIONS[SHOPIFY].TAGS.CHANGE_TO: {
        return updates.add(MENU_ITEM.TAGS);
      }

      case OPERATIONS[SHOPIFY].TITLE.SET: {
        return updates.add(MENU_ITEM.TITLE);
      }

      case OPERATIONS[SHOPIFY].VARIATIONS: {
        return updates.add(MENU_ITEM.VARIATIONS);
      }

      case OPERATIONS[SHOPIFY].PRICE.CHANGE_TO:
      case OPERATIONS[SHOPIFY].CAP.CHANGE_TO:
      case OPERATIONS[SHOPIFY].CPI.CHANGE_TO:
      case OPERATIONS[SHOPIFY].CHARGE_TAX.SET: {
        return updates.add(MENU_ITEM.PRICE);
      }

      case OPERATIONS[SHOPIFY].QUANTITY.CHANGE_TO:
      case OPERATIONS[SHOPIFY].TRACK_QUANTITY.SET:
      case OPERATIONS[SHOPIFY].CONTINUE_SELLING.SET: {
        return updates.add(MENU_ITEM.QUANTITY);
      }

      case OPERATIONS[SHOPIFY].SKU.CHANGE_TO: {
        return updates.add(MENU_ITEM.SKU);
      }

      case OPERATIONS[SHOPIFY].BARCODE.CHANGE_TO: {
        return updates.add(MENU_ITEM.BARCODE);
      }

      case OPERATIONS[SHOPIFY].COUNTRY_CODE.SET: {
        return updates.add(MENU_ITEM.COUNTRY_CODE);
      }

      case OPERATIONS[SHOPIFY].HS_CODE.SET: {
        return updates.add(MENU_ITEM.HS_CODE);
      }

      case OPERATIONS[SHOPIFY].WEIGHT.CHANGE_TO: {
        return updates.add(MENU_ITEM.WEIGHT);
      }

      case OPERATIONS[SHOPIFY].VENDOR: {
        return updates.add(MENU_ITEM.VENDOR);
      }

      case OPERATIONS[SHOPIFY].VIDEOS.CHANGE_TO: {
        return updates.add(MENU_ITEM.VIDEOS);
      }

      default: {
        return updates;
      }
    }
  }

  switch (channel) {
    case EBAY: {
      return operations.reduce(reduceEbayOperations, EMPTY.SET);
    }

    case ETSY: {
      return operations.reduce(reduceEtsyOperations, EMPTY.SET);
    }

    case FAIRE: {
      return operations.reduce(reduceFaireOperations, EMPTY.SET);
    }

    case SHOPIFY: {
      return operations.reduce(reduceShopifyOperations, EMPTY.SET);
    }

    default: {
      return EMPTY.SET;
    }
  }
}
